import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { PageHeader, Card, EmptyState } from "../components/admin/AdminUI";

const typeLabel = {
  early_registration: "Early Registration",
  masterclass: "Masterclass",
  conference: "Conference",
  openclass: "Open Class",
  orientation: "Orientation",
  graduation: "Graduation",
};

// Tipe banner yang punya jam acara (start wajib, end opsional) — dilampirkan di
// email konfirmasi pendaftar event tsb.
const EVENT_TYPES = ["masterclass", "conference", "openclass", "orientation"];

const BannerPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMe, setErrorMe] = useState(false);

  const [bannerVisible, setBannerVisible] = useState(true);
  const [visibilityLoading, setVisibilityLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [type, setType] = useState("early_registration");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePortraitFile, setImagePortraitFile] = useState(null);
  const [imagePortraitPreview, setImagePortraitPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [zoomLink, setZoomLink] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [zoomPasscode, setZoomPasscode] = useState("");
  const [eventStartTime, setEventStartTime] = useState("");
  const [eventEndTime, setEventEndTime] = useState("");

  const [editBanner, setEditBanner] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editSubtitle, setEditSubtitle] = useState("");
  const [editType, setEditType] = useState("early_registration");
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editImagePortraitFile, setEditImagePortraitFile] = useState(null);
  const [editImagePortraitPreview, setEditImagePortraitPreview] = useState(null);
  const [editRemovePortrait, setEditRemovePortrait] = useState(false);
  const [editZoomLink, setEditZoomLink] = useState("");
  const [editMeetingId, setEditMeetingId] = useState("");
  const [editZoomPasscode, setEditZoomPasscode] = useState("");
  const [editEventStartTime, setEditEventStartTime] = useState("");
  const [editEventEndTime, setEditEventEndTime] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch {
      setErrorMe(true);
    }
  };

  const fetchBanners = async () => {
    try {
      const res = await axios.get("/bannersadmin");
      setBanners(res.data);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVisibility = async () => {
    try {
      const res = await axios.get("/banner/visibility");
      setBannerVisible(res.data.visible);
    } catch {}
  };

  const handleToggleVisibility = async () => {
    setVisibilityLoading(true);
    try {
      await axios.patch("/banner/visibility", { visible: !bannerVisible });
      setBannerVisible((prev) => !prev);
    } catch {
      Swal.fire({ text: "Failed to update visibility.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } finally {
      setVisibilityLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin({ pathname: "/bannerpage" });
    getMe();
    fetchBanners();
    fetchVisibility();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account!",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleImagePortraitChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagePortraitFile(file);
    setImagePortraitPreview(URL.createObjectURL(file));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      Swal.fire({ text: "Please select an image.", icon: "warning", background: "white", color: "black", confirmButtonColor: "#fb3104" });
      return;
    }
    if (!title) {
      Swal.fire({ text: "Title is required.", icon: "warning", background: "white", color: "black", confirmButtonColor: "#fb3104" });
      return;
    }
    if (EVENT_TYPES.includes(type) && !eventStartTime) {
      Swal.fire({ text: "Event start time is required for this event type.", icon: "warning", background: "white", color: "black", confirmButtonColor: "#fb3104" });
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("image", imageFile);
      if (imagePortraitFile) formData.append("imagePortrait", imagePortraitFile);
      formData.append("type", type);
      formData.append("title", title);
      formData.append("subtitle", subtitle);
      if (type === "openclass") {
        formData.append("zoomLink", zoomLink);
        formData.append("meetingId", meetingId);
        formData.append("zoomPasscode", zoomPasscode);
      }
      if (EVENT_TYPES.includes(type)) {
        formData.append("eventStartTime", eventStartTime);
        formData.append("eventEndTime", eventEndTime);
      }
      await axios.post("/banner", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTitle("");
      setSubtitle("");
      setType("early_registration");
      setImageFile(null);
      setImagePreview(null);
      setImagePortraitFile(null);
      setImagePortraitPreview(null);
      setZoomLink("");
      setMeetingId("");
      setZoomPasscode("");
      setEventStartTime("");
      setEventEndTime("");
      setFileInputKey((k) => k + 1);
      await fetchBanners();
      Swal.fire({ text: "Banner created!", icon: "success", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } catch (err) {
      Swal.fire({ text: err.response?.data?.msg || "Failed to create banner.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } finally {
      setSubmitting(false);
    }
  };

  const openEditBanner = (banner) => {
    setEditBanner(banner);
    setEditTitle(banner.title || "");
    setEditSubtitle(banner.subtitle || "");
    setEditType(banner.type || "early_registration");
    setEditImageFile(null);
    setEditImagePreview(null);
    setEditImagePortraitFile(null);
    setEditImagePortraitPreview(null);
    setEditRemovePortrait(false);
    setEditZoomLink(banner.zoomLink || "");
    setEditMeetingId(banner.meetingId || "");
    setEditZoomPasscode(banner.zoomPasscode || "");
    setEditEventStartTime(banner.eventStartTime || "");
    setEditEventEndTime(banner.eventEndTime || "");
  };

  const closeEditBanner = () => {
    setEditBanner(null);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditImageFile(file);
    setEditImagePreview(URL.createObjectURL(file));
  };

  const handleEditImagePortraitChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditImagePortraitFile(file);
    setEditImagePortraitPreview(URL.createObjectURL(file));
    setEditRemovePortrait(false);
  };

  const handleUpdateBanner = async () => {
    if (!editTitle) {
      Swal.fire({ text: "Title is required.", icon: "warning", background: "white", color: "black", confirmButtonColor: "#fb3104" });
      return;
    }
    if (EVENT_TYPES.includes(editType) && !editEventStartTime) {
      Swal.fire({ text: "Event start time is required for this event type.", icon: "warning", background: "white", color: "black", confirmButtonColor: "#fb3104" });
      return;
    }
    setEditSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("type", editType);
      formData.append("title", editTitle);
      formData.append("subtitle", editSubtitle);
      if (editImageFile) {
        formData.append("image", editImageFile);
      }
      if (editImagePortraitFile) {
        formData.append("imagePortrait", editImagePortraitFile);
      } else if (editRemovePortrait) {
        formData.append("removePortrait", "true");
      }
      if (editType === "openclass") {
        formData.append("zoomLink", editZoomLink);
        formData.append("meetingId", editMeetingId);
        formData.append("zoomPasscode", editZoomPasscode);
      }
      if (EVENT_TYPES.includes(editType)) {
        formData.append("eventStartTime", editEventStartTime);
        formData.append("eventEndTime", editEventEndTime);
      }
      await axios.put(`/banner/${editBanner.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      closeEditBanner();
      await fetchBanners();
      Swal.fire({ text: "Banner updated!", icon: "success", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } catch (err) {
      Swal.fire({ text: err.response?.data?.msg || "Failed to update banner.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await axios.patch(`/banner/${id}`);
      setBanners((prev) =>
        prev.map((b) => (b.id === id ? { ...b, isActive: !currentStatus } : b))
      );
    } catch {
      Swal.fire({ text: "Failed to update banner.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      text: "Delete this banner? This cannot be undone.",
      icon: "warning",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`/banner/${id}`);
      setBanners((prev) => prev.filter((b) => b.id !== id));
      Swal.fire({ text: "Banner deleted.", icon: "success", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    } catch {
      Swal.fire({ text: "Failed to delete banner.", icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });
    }
  };

  const typeOptions = (
    <>
      <option value="early_registration">Early Registration</option>
      <option value="masterclass">Masterclass</option>
      <option value="conference">Conference</option>
      <option value="openclass">Open Class</option>
      <option value="orientation">Orientation</option>
      <option value="graduation">Graduation</option>
    </>
  );

  const eventSchedulePanel = (start, setStart, end, setEnd) => (
    <div className="adm-panel">
      <p className="adm-panel-title">Event schedule</p>
      <p className="adm-help" style={{ margin: "-4px 0 12px" }}>Included in the registrant's confirmation email.</p>
      <div className="d-flex gap-3 flex-wrap">
        <div style={{ width: "170px" }}>
          <label className="form-label">
            Start Time <span style={{ color: "var(--adm-danger)" }}>*</span>
          </label>
          <input type="time" className="form-control" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div style={{ width: "170px" }}>
          <label className="form-label">
            End Time <span style={{ color: "var(--adm-muted)", fontWeight: 400 }}>(optional)</span>
          </label>
          <input type="time" className="form-control" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>
    </div>
  );

  const zoomPanel = (link, setLink, id, setId, pass, setPass, note) => (
    <div className="adm-panel">
      <p className="adm-panel-title">Zoom meeting info</p>
      <p className="adm-help" style={{ margin: "-4px 0 12px" }}>For Online Open Class participants.</p>
      <div className="adm-form-grid adm-form-grid--3">
        <div>
          <label className="form-label">Zoom Link</label>
          <input type="text" className="form-control" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://zoom.us/j/..." />
        </div>
        <div>
          <label className="form-label">Meeting ID</label>
          <input type="text" className="form-control" value={id} onChange={(e) => setId(e.target.value)} placeholder="Optional" />
        </div>
        <div>
          <label className="form-label">Passcode</label>
          <input type="text" className="form-control" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Optional" />
        </div>
      </div>
      {note && <p className="adm-help" style={{ marginTop: "10px" }}>{note}</p>}
    </div>
  );

  return (
    <div className="adm-main">
      <PageHeader
        title="Banners"
        subtitle="Banners shown in the homepage slider. Each banner's button opens the matching registration form."
        actions={
          <label className={`adm-switch-pill${bannerVisible ? " adm-switch-pill--on" : ""}`}>
            <span>Homepage slider</span>
            <span className="form-check form-switch m-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={!!bannerVisible}
                disabled={visibilityLoading}
                onChange={handleToggleVisibility}
              />
            </span>
            <strong>{bannerVisible ? "On" : "Off"}</strong>
          </label>
        }
      />

      {/* ── Daftar banner ── */}
      <Card title="Existing Banners" subtitle={`${banners.length} banner${banners.length === 1 ? "" : "s"}`} flush>
        {isLoading ? (
          <EmptyState icon="bi-hourglass" title="Loading banners…" />
        ) : banners.length === 0 ? (
          <EmptyState icon="bi-image" title="No banners yet" text="Upload your first banner with the form below." />
        ) : (
          <div>
            {banners.map((banner) => (
              <div key={banner.id} className={`adm-list-row${banner.isActive ? "" : " adm-list-row--muted"}`}>
                <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                  <img className="adm-thumb" src={banner.imageUrl} alt={banner.title} style={{ width: "150px", height: "75px" }} />
                  {banner.imagePortraitUrl && (
                    <img className="adm-thumb" src={banner.imagePortraitUrl} alt={`${banner.title} portrait`} style={{ width: "42px", height: "75px" }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="adm-cell-title">{banner.title}</div>
                  {banner.subtitle && <div className="adm-cell-meta">{banner.subtitle}</div>}
                  <div className="adm-badges">
                    <span className={`adm-status ${banner.isActive ? "adm-status--success" : ""}`}>
                      {banner.isActive ? "Active" : "Inactive"}
                    </span>
                    <span className="adm-badge adm-badge--primary">{typeLabel[banner.type]}</span>
                    {banner.imagePortraitUrl && <span className="adm-badge adm-badge--info">Has portrait</span>}
                  </div>
                  {banner.type === "openclass" && (
                    <div className="adm-cell-meta" style={{ marginTop: "6px" }}>
                      {banner.zoomLink
                        ? `Zoom: ${banner.zoomLink}${banner.meetingId ? ` | ID: ${banner.meetingId}` : ""}${banner.zoomPasscode ? ` | Pass: ${banner.zoomPasscode}` : ""}`
                        : "Zoom info not set yet"}
                    </div>
                  )}
                </div>
                <div className="adm-actions">
                  <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => handleToggle(banner.id, banner.isActive)}>
                    {banner.isActive ? "Deactivate" : "Activate"}
                  </button>
                  <button type="button" className="adm-icon-btn" title="Edit banner" aria-label="Edit banner" onClick={() => openEditBanner(banner)}>
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    type="button"
                    className="adm-icon-btn adm-icon-btn--danger"
                    title="Delete banner"
                    aria-label="Delete banner"
                    onClick={() => handleDelete(banner.id)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Upload banner baru ── */}
      <Card title="Upload New Banner" className="adm-section">
        <form onSubmit={handleCreate}>
          <div className="adm-form-grid">
            <div>
              <label className="form-label">
                Title <span style={{ color: "var(--adm-primary)" }}>*</span>
              </label>
              <input type="text" className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Early Registration Open!" />
            </div>
            <div>
              <label className="form-label">Subtitle</label>
              <input type="text" className="form-control" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="e.g. Register before Dec 31" />
            </div>
            <div>
              <label className="form-label">
                Type <span style={{ color: "var(--adm-primary)" }}>*</span>
              </label>
              <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
                {typeOptions}
              </select>
            </div>
            <div />
            <div>
              <label className="form-label">
                Banner Image (Landscape) <span style={{ color: "var(--adm-primary)" }}>*</span>
              </label>
              <input key={fileInputKey} type="file" className="form-control" accept="image/*" onChange={handleImageChange} />
              <p className="adm-help">Max 5MB. JPG, PNG, or WebP. Recommended ratio 16:5 (e.g. 1600×500px).</p>
              {imagePreview && (
                <img className="adm-thumb" src={imagePreview} alt="preview" style={{ width: "100%", maxHeight: "180px", marginTop: "10px" }} />
              )}
            </div>
            <div>
              <label className="form-label">
                Portrait Photo <span style={{ color: "var(--adm-muted)", fontWeight: 400 }}>(optional)</span>
              </label>
              <input key={fileInputKey + 1} type="file" className="form-control" accept="image/*" onChange={handleImagePortraitChange} />
              <p className="adm-help">Shown on mobile portrait view. Ratio 9:16. Leave empty to use the landscape photo everywhere.</p>
              {imagePortraitPreview && (
                <img className="adm-thumb" src={imagePortraitPreview} alt="portrait preview" style={{ width: "120px", maxHeight: "220px", marginTop: "10px" }} />
              )}
            </div>
            {EVENT_TYPES.includes(type) && (
              <div className="adm-span-all">{eventSchedulePanel(eventStartTime, setEventStartTime, eventEndTime, setEventEndTime)}</div>
            )}
            {type === "openclass" && (
              <div className="adm-span-all">
                {zoomPanel(
                  zoomLink,
                  setZoomLink,
                  meetingId,
                  setMeetingId,
                  zoomPasscode,
                  setZoomPasscode,
                  "Info ini otomatis dikirim lewat email ke setiap peserta Open Class yang mendaftar Online, selama banner ini aktif.",
                )}
              </div>
            )}
          </div>
          <div className="adm-form-actions">
            <button type="submit" className="adm-btn adm-btn--primary" disabled={submitting}>
              <i className="bi bi-upload"></i>
              {submitting ? "Uploading..." : "Upload Banner"}
            </button>
          </div>
        </form>
      </Card>

      {editBanner &&
        createPortal(
          <div className="adm-modal-backdrop">
            <div className="adm-modal" style={{ width: "560px" }}>
              <div className="adm-modal-header">
                <h5 className="adm-card-title" style={{ fontSize: "17px" }}>Edit Banner</h5>
                <button type="button" className="adm-modal-close" aria-label="Close" onClick={closeEditBanner} disabled={editSubmitting}>
                  ×
                </button>
              </div>

              <div className="adm-modal-body d-flex flex-column gap-3">
                <div>
                  <label className="form-label">
                    Title <span style={{ color: "var(--adm-primary)" }}>*</span>
                  </label>
                  <input type="text" className="form-control" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Subtitle</label>
                  <input type="text" className="form-control" value={editSubtitle} onChange={(e) => setEditSubtitle(e.target.value)} />
                </div>
                <div>
                  <label className="form-label">
                    Type <span style={{ color: "var(--adm-primary)" }}>*</span>
                  </label>
                  <select className="form-select" value={editType} onChange={(e) => setEditType(e.target.value)}>
                    {typeOptions}
                  </select>
                </div>
                <div>
                  <label className="form-label">Banner Image (Landscape)</label>
                  <input type="file" className="form-control" accept="image/*" onChange={handleEditImageChange} />
                  <p className="adm-help">Leave empty to keep the current image.</p>
                  <img className="adm-thumb" src={editImagePreview || editBanner.imageUrl} alt="preview" style={{ width: "100%", maxHeight: "160px", marginTop: "10px" }} />
                </div>
                <div>
                  <label className="form-label">
                    Portrait Photo <span style={{ color: "var(--adm-muted)", fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input type="file" className="form-control" accept="image/*" onChange={handleEditImagePortraitChange} />
                  <p className="adm-help">Leave empty to keep the current portrait photo (if any).</p>
                  {(editImagePortraitPreview || (editBanner.imagePortraitUrl && !editRemovePortrait)) && (
                    <img
                      className="adm-thumb"
                      src={editImagePortraitPreview || editBanner.imagePortraitUrl}
                      alt="portrait preview"
                      style={{ width: "120px", maxHeight: "220px", marginTop: "10px" }}
                    />
                  )}
                  {editBanner.imagePortraitUrl && !editImagePortraitFile && (
                    <label className="adm-check" style={{ marginTop: "10px", fontSize: "13px" }}>
                      <input type="checkbox" checked={editRemovePortrait} onChange={(e) => setEditRemovePortrait(e.target.checked)} />
                      Remove portrait photo (use the landscape photo on mobile too)
                    </label>
                  )}
                </div>
                {EVENT_TYPES.includes(editType) &&
                  eventSchedulePanel(editEventStartTime, setEditEventStartTime, editEventEndTime, setEditEventEndTime)}
                {editType === "openclass" &&
                  zoomPanel(editZoomLink, setEditZoomLink, editMeetingId, setEditMeetingId, editZoomPasscode, setEditZoomPasscode)}
              </div>

              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn--secondary" onClick={closeEditBanner} disabled={editSubmitting}>
                  Cancel
                </button>
                <button type="button" className="adm-btn adm-btn--primary" onClick={handleUpdateBanner} disabled={editSubmitting}>
                  {editSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default BannerPage;
